import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Select, Space, message, Tag, Form, Input } from 'antd';
import { agentApi, agentServiceApi, type AgentDTO, type AgentServiceChannelDTO } from '../api/kfc';
import { clientTablePagination } from '../utils/tablePagination';

type SearchValues = {
  agentMongoId?: string;
  stype?: string;
  serviceName?: string;
};

const matchContains = (hay?: string, needle?: string) =>
  !needle?.trim() || (hay ?? '').toLowerCase().includes(needle.trim().toLowerCase());

const AgentServiceList: React.FC = () => {
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [services, setServices] = useState<AgentServiceChannelDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncAgentId, setSyncAgentId] = useState<string | null>(null);
  const [appliedSearch, setAppliedSearch] = useState<SearchValues>({});
  const [searchForm] = Form.useForm<SearchValues>();

  const loadAgents = () => {
    agentApi.list().then((res) => setAgents(res.data)).catch(() => setAgents([]));
  };

  const loadServices = (agentId?: string) => {
    setLoading(true);
    agentServiceApi
      .list(agentId)
      .then((res) => setServices(res.data))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAgents();
    loadServices();
  }, []);

  const agentIdToIdentifier = (mongoId?: string) => {
    if (!mongoId) return undefined;
    const a = agents.find((x) => x.id === mongoId);
    return a?.basicInfo?.identifier ?? mongoId;
  };

  const agentOptions = agents.map((a) => ({
    value: a.id!,
    label: `${a.basicInfo?.name ?? ''} (${a.basicInfo?.identifier ?? a.id})`,
  }));

  const filterOption = (input: string, opt?: { label?: string }) =>
    (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase());

  const onSearch = () => {
    const values = searchForm.getFieldsValue();
    const next: SearchValues = {
      agentMongoId: values.agentMongoId,
      stype: values.stype?.trim() || undefined,
      serviceName: values.serviceName?.trim() || undefined,
    };
    setAppliedSearch(next);
    loadServices(next.agentMongoId);
  };

  const onReset = () => {
    searchForm.resetFields();
    setAppliedSearch({});
    loadServices();
  };

  const sync = (agentId: string) => {
    setSyncingId(agentId);
    agentServiceApi
      .syncFromAgent(agentId)
      .then((res) => {
        message.success(res.data.success ? res.data.message : (res.data.message || '同步失败'));
        if (res.data.success) loadServices(appliedSearch.agentMongoId);
      })
      .catch((e) => message.error(e.response?.data?.message || '同步失败'))
      .finally(() => setSyncingId(null));
  };

  const filteredServices = useMemo(() => {
    const { agentMongoId, stype, serviceName } = appliedSearch;
    const ident = agentIdToIdentifier(agentMongoId);
    const agentIds = agentMongoId
      ? [agentMongoId, ident].filter((x): x is string => !!x)
      : null;

    return services.filter((s) => {
      const agentOk = !agentIds || (s.agentId != null && agentIds.includes(s.agentId));
      return agentOk && matchContains(s.stype, stype) && matchContains(s.serviceName, serviceName);
    });
  }, [services, appliedSearch, agents]);

  return (
    <Card
      title="代理渠道列表"
      extra={
        <Space>
          <Select
            placeholder="选择代理"
            allowClear
            showSearch
            style={{ minWidth: 200 }}
            value={syncAgentId}
            onChange={setSyncAgentId}
            options={agentOptions}
            filterOption={filterOption}
          />
          <Button
            type="primary"
            onClick={() => syncAgentId && sync(syncAgentId)}
            loading={syncingId !== null}
            disabled={!syncAgentId}
          >
            同步渠道
          </Button>
        </Space>
      }
    >
      <Form
        form={searchForm}
        layout="inline"
        style={{ marginBottom: 16 }}
        onFinish={onSearch}
      >
        <Form.Item name="agentMongoId" label="代理">
          <Select
            placeholder="代理名称或ID"
            allowClear
            showSearch
            style={{ minWidth: 220 }}
            options={agentOptions}
            filterOption={filterOption}
          />
        </Form.Item>
        <Form.Item name="stype" label="服务类型">
          <Input placeholder="模糊搜索" allowClear style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name="serviceName" label="渠道名称">
          <Input placeholder="模糊搜索" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              查找
            </Button>
            <Button onClick={onReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        loading={loading}
        dataSource={filteredServices}
        rowKey={(r) => r.id ?? `${r.agentId}-${r.serviceId}`}
        scroll={{ x: 800 }}
        columns={[
          { title: '代理ID', dataIndex: 'agentId', width: 120, ellipsis: true },
          { title: '服务ID', dataIndex: 'serviceId', width: 90 },
          { title: '渠道编码', dataIndex: 'serviceCode', width: 140, ellipsis: true },
          { title: '服务渠道名称', dataIndex: 'serviceName', width: 160, ellipsis: true },
          {
            title: '状态',
            dataIndex: 'serviceStatus',
            width: 80,
            render: (v: number) =>
              v === 0 ? <Tag color="green">启用</Tag> : <Tag color="default">禁用</Tag>,
          },
          { title: '国家', dataIndex: 'country', width: 120, ellipsis: true },
          { title: '服务类型', dataIndex: 'stype', width: 120, ellipsis: true },
        ]}
        pagination={clientTablePagination({ defaultPageSize: 20 })}
      />
    </Card>
  );
};

export default AgentServiceList;
